<template>
    <div>
        <h1>License Search</h1>
        <div class="card overflow-x-scroll">
            <div class="card-header">Search Current Licenses</div>
            <div class="card-body">
                <form-summary :validator="$v" ref="errorContainer" id="errorContainer"/>
                <b-row>
                    <b-col class="font-weight-bold mb-2">
                        For information regarding disciplinary actions, please visit the
                        <a href="https://www.health.state.mn.us/facilities/providers/bodyart/licenses/disciplinary.html" target="_blank">
                            disciplinary action page (opens new tab)
                        </a>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12" md="4" id="othAttach">
                        <form-group label="Select License Category" label-for="licenseSelect" :validator="$v.cat">
                            <b-form-select
                                    @change="loadLicenses"
                                    :options="licenseTypes"
                                    v-model="cat"
                                    id="licenseSelect"
                            >
                                <template v-slot:first>
                                    <option :value="null" disabled>-- Please select an option --</option>
                                </template>
                            </b-form-select>
                        </form-group>
                    </b-col>
                </b-row>
                <div v-if="cat != null" class="pt-3">
                    <b-row>
                        <b-col>
                            <h2>License List</h2>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="6">
                            <b-form-group
                                    label="Per page"
                                    label-size="sm"
                                    label-for="perPageSelect"
                            >
                                <b-form-select
                                        v-model="perPage"
                                        id="perPageSelect"
                                        size="sm"
                                        :options="pageOptions"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col cols="6">
                            <b-form-group
                                    label="Search"
                                    label-size="sm"
                                    label-for="filterInput"
                            >
                                <b-input-group size="sm">
                                    <b-form-input
                                            v-model="filter"
                                            type="search"
                                            id="filterInput"
                                            placeholder="Search"
                                    ></b-form-input>
                                    <b-input-group-append>
                                        <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row role="alert">
                        <b-col>
                            <div class="sr-only hidden" v-if="isLoading">Loading, Please Wait...</div>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-table hover small show-empty class="sr-caption"
                                     aria-live="polite"
                                     responsive="true"
                                     :fields="fields"
                                     :items="licenses"
                                     type="search"
                                     :current-page="currentPage"
                                     :per-page="perPage"
                                     :filter="filter"
                                     @filtered="onFiltered"
                                     :filter-included-fields="filterFields"
                                     :busy="isLoading"
                                     label-sort-asc=""
                                     label-sort-desc=""
                                     caption="License Search table.  To sort the data in this table by a specific column,
                                     go to the appropriate column header cell and use enter to sort the data ascending.
                                     Use enter again to sort descending."
                            >
                                <template v-slot:no-data>
                                    No Licenses Found.
                                </template>
                                <div slot="table-busy" class="text-center text-danger my-2">
                                    <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                    <strong>Loading...</strong>
                                </div>
                                <template slot="empty" slot-scope="scope">
                                    No Licenses Found.
                                </template>
                                <template v-slot:cell(workDates)="row">
                                    <p class="mt-0 mb-1" v-for="(date, index) in row.item.workDates" :key="index">
                                        {{date}}
                                    </p>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-pagination
                                    v-model="currentPage"
                                    :per-page="perPage"
                                    :total-rows="totalRows"
                                    align="fill"
                                    size="sm"
                                    class="my-0"
                            ></b-pagination>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import axios from "axios"
    import axiosRetry from 'axios-retry'

    axiosRetry(axios, { retries: 3,
        retryDelay: axiosRetry.exponentialDelay  })

    export default {
        data() {
            return {
                licenses: [],
                licenseTypes: ['GUEST', 'TEMPORARY', 'FULL'],
                cat: null,
                fields: [],
                filterFields: [],
                perPage: 5,
                currentPage: 1,
                totalRows: 1,
                pageOptions: [5, 10, 15, 20],
                filter: null,
                isLoading: false,
            }
        },
        methods: {
            loadLicenses() {
                this.isLoading = true
                this.updateFields()
                axios.get(`${process.env.VUE_APP_API_BASE_URL}/license/subCategory/${this.cat}`,
                    (this.$keycloak && this.$keycloak.authenticated) ? {} : { ignoreHeader: true })
                    .then(response => {
                        this.licenses = response.data
                        this.updatePagination()
                        this.isLoading = false
                    })
            },
            updateFields() {
                this.fields = [
                    {key: "lastName", label: "Last Name", sortable: true},
                    {key: "firstName", label: "First Name", sortable: true},
                    {key: "middleName", label: "Middle Name", sortable: true},
                    {key: "number", label: "License No.", sortable: true},
                    {key: "status", label: "Status", sortable: true},
                    {key: "otherNames", label: "Previous Name(s)", sortable: true},
                    {key: "tradeNames", label: "Trade Name(s)", sortable: true},
                ]
              if (this.cat !== 'GUEST') {
                  this.fields.splice(this.fields.length - 2, 0,
                      {key: "effectiveDate", label: "Effective Date", sortable: true},)
                  this.fields.splice(this.fields.length - 2, 0,
                      {key: "expirationDate", label: "Expiration Date", sortable: true}, )
                  this.fields.push(
                      {key: "licenseType.description", label: "License Type", sortable: true},
                      {key: "formattedStatus", label: "License Status", sortable: true},
                  )
                  if (this.cat === 'TEMPORARY') {
                      this.fields.push([
                          {key: "mnSupervisor", label: "Supervisor", sortable: true},
                      ])
                  }
              } else {
                  this.fields.push([
                      {key: "workDates", label: "Work Dates"},
                  ])
              }
              this.filterFields = this.updateFilter();
            },
            onFiltered(filteredItems) {
                // Trigger pagination to update the number of buttons/pages due to filtering
                this.totalRows = filteredItems.length
                this.currentPage = 1
            },
            updatePagination() {
                this.totalRows = this.licenses.length
                this.currentPage = 1
            },
            updateFilter() {
                var fields = ["lastName", "firstName", "middleName", "number", "effectiveDate", "expirationDate", "otherNames", "tradeNames"];
                if (this.cat !== 'GUEST') {
                    fields.push("licenseType.description");
                    fields.push("formattedStatus");
                    if (this.cat === 'TEMPORARY') {
                        fields.push("mnSupervisor");
                    }
                }
                return fields;
            },
        },
        validations() {
            return {
                cat:{ required: false }
            }
        },
        created() {
            document.title = "License Search - Body Art"
        }
    }
</script>
<style>
    .overflow-x-scroll {
        overflow-x: auto;
    }
</style>